<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-4 sm:p-6 shadow-lg border border-gray-700 animate-scaleIn flex flex-col gap-4">
      <!-- Loading & Error -->
      <div v-if="isLoading" class="flex flex-col items-center py-6">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
        <p class="text-gray-300 text-sm mt-2">กำลังโหลดข้อมูล...</p>
      </div>
      <div v-else-if="error" class="flex flex-col gap-4">
        <div class="text-center text-red-300 py-6 text-sm">{{ error }}</div>
        <button
          @click="$emit('close')"
          class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600 flex items-center justify-center gap-2"
        >
          <i class="fas fa-times text-xs"></i> ปิด
        </button>
      </div>
      <!-- Main Content -->
      <div v-else class="flex flex-col gap-4">
        <!-- Tab: Home -->
        <div v-if="activeTab === 'home'" class="flex flex-col gap-4">
          <!-- Survivor Profile Card -->
          <div class="bg-gray-800 rounded-md p-3 border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900">
            <div class="flex items-center gap-3">
              <div class="relative">
                <img
                  :src="displayImage"
                  alt="Survivor"
                  class="w-16 h-16 rounded-full object-cover border-2 border-gray-600"
                />
                <div
                  class="absolute -bottom-1 -right-1 bg-indigo-600 text-white text-xxs sm:text-xs font-semibold rounded-full w-6 h-6 flex items-center justify-center border-2 border-gray-800"
                >
                  {{ survivor?.level || 'N/A' }}
                </div>
              </div>
              <div class="flex flex-col flex-1 min-w-0">
                <h3 class="text-gray-300 text-base sm:text-lg font-semibold truncate">{{ displayName }}</h3>
                <!-- Guardian Ghost Protection Display -->
                <div v-if="survivorEvent && survivorEvent.type === 'guardianGhost'" class="mt-2 p-2 bg-green-900 bg-opacity-50 rounded-md border border-green-700">
                  <p class="text-green-300 text-xs font-semibold flex items-center gap-1">
                    <i class="fas fa-shield-alt"></i>
                    วิญญาณผู้พิทักษ์: {{ survivorEvent.ghost.name }} (เกราะ: {{ survivorEvent.ghost.protectCount }})
                  </p>
                  <p class="text-green-400 text-xxs mt-1">ปกป้องจนกว่าจะหมดเกราะ</p>
                </div>
                <div class="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2 text-xxs sm:text-xs text-gray-300">
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-heart text-sm text-indigo-400"></i>
                    <p class="mt-0.5">??/??</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-fist-raised text-sm text-indigo-400"></i>
                    <p class="mt-0.5">??</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-running text-sm text-indigo-400"></i>
                    <p class="mt-0.5">??</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-crosshairs text-sm text-indigo-400"></i>
                    <p class="mt-0.5">??</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fa-solid fa-shield-virus text-sm text-indigo-400"></i>
                    <p class="mt-0.5">??</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-shield-alt text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ displayProtectCount }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Reward Section (Hidden for anonymous buildings) -->
          <div v-if="false" class="bg-gray-800">
            <p class="text-center text-gray-300 text-sm font-semibold mb-3 border-b border-gray-700 pb-2">
              รางวัลเมื่อลอบสังหารสำเร็จ
            </p>
            <!-- Show loss percentage explanation -->
            <div class="text-center text-xxs text-gray-400 mb-2">
              ระยะห่างของห้อง: {{ roomDifference }} → อัตราส่วนที่ถูกยึด: {{ lossPercent }}%
            </div>
            <div class="grid grid-cols-2 gap-3">
              <!-- Diamond Reward -->
              <div
                v-if="displayDiamond > 0"
                class="flex flex-col items-center bg-gray-800 p-3 rounded-md border border-gray-700"
              >
                <i class="fas fa-gem text-blue-400 text-lg mb-2"></i>
                <span class="text-xxs sm:text-xs text-gray-300 font-semibold">
                  จากผู้รอดชีวิต: {{ displayDiamond }} เพชร
                </span>
                <span class="text-xxs sm:text-xs text-indigo-400 mt-1">
                  ถูกยึด: {{ diamondStolen }} เพชร (ก่อนหักภาษี)
                </span>
                <span class="text-xxs sm:text-xs text-gray-400">
                  ภาษี: {{ diamondTax }} เพชร
                </span>
                <span class="text-xxs sm:text-xs text-green-300 mt-1 font-semibold">
                  ได้รับสุทธิ: {{ netDiamond }} เพชร
                </span>
              </div>
              <!-- Gold Reward -->
              <div
                v-if="displayGold > 0"
                class="flex flex-col items-center bg-gray-800 p-3 rounded-md border border-gray-700"
              >
                <i class="fas fa-g text-yellow-400 text-lg mb-2"></i>
                <span class="text-xxs sm:text-xs text-gray-300 font-semibold">
                  จากผู้รอดชีวิต: {{ displayGold }} ทอง
                </span>
                <span class="text-xxs sm:text-xs text-yellow-400 mt-1">
                  ถูกยึด: {{ goldStolen }} ทอง (ก่อนหักภาษี)
                </span>
                <span class="text-xxs sm:text-xs text-gray-400">
                  ภาษี: {{ goldTax }} ทอง
                </span>
                <span class="text-xxs sm:text-xs text-green-300 mt-1 font-semibold">
                  ได้รับสุทธิ: {{ netGold }} ทอง
                </span>
              </div>
            </div>
            <!-- Summary -->
            <p class="text-center text-gray-300 text-xs mt-3">
              สรุป: ได้สุทธิ {{ netDiamond }} เพชร และ {{ netGold }} ทอง (ภาษี 30% ถูกหักแล้ว)
            </p>
          </div>
          <!-- Player Profile Card -->
          <div class="bg-gray-800 rounded-md p-3 border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900">
            <div class="flex items-center gap-3">
              <div class="relative">
                <img
                  :src="characterPublic.imageUrl || 'default-character.png'"
                  alt="Player Character"
                  class="w-16 h-16 rounded-full object-cover border-2 border-gray-600"
                />
                <div
                  class="absolute -bottom-1 -right-1 bg-indigo-600 text-white text-xxs sm:text-xs font-semibold rounded-full w-6 h-6 flex items-center justify-center border-2 border-gray-800"
                >
                  {{ characterPublic?.level || 'N/A' }}
                </div>
              </div>
              <div class="flex flex-col flex-1 min-w-0">
                <h3 class="text-gray-300 text-base sm:text-lg font-semibold truncate">{{ characterPublic.characterName || 'Unknown' }}</h3>
                <div class="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2 text-xxs sm:text-xs text-gray-300">
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-heart text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ playerHp }}/{{ playerMaxHp }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-fist-raised text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.atk }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-running text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.evasion }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-crosshairs text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.critical }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fa-solid fa-shield-virus text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.criticalReduction }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-shield-alt text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.defense }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Buttons in Tab: Home -->
          <div class="flex flex-col gap-2">
            <button
              @click="activeTab = 'assassination'"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700"
              :disabled="!isPvpEnabled"
            >
              <i class="fas fa-user-ninja text-xs"></i>
              <span v-if="isPvpEnabled">ดำเนินการลอบสังหาร</span>
              <span v-else>ขณะนี้ปิดระบบ PVP ชั่วคราว</span>
            </button>
            <button
              @click="$emit('close')"
              class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600 flex items-center justify-center gap-2"
            >
              <i class="fas fa-times text-xs"></i> ปิด
            </button>
          </div>
        </div>
        <!-- Tab: Assassination Menu -->
        <div v-else-if="activeTab === 'assassination'" class="flex flex-col gap-4">
          <!-- Assassination Attempts Display -->
          <div class="bg-gray-800 rounded-md">
            <div class="flex justify-between items-center gap-4">
              <!-- Total Attempts -->
              <div class="flex items-center gap-2 bg-gray-800 p-3 w-full border border-gray-700">
                <i class="fas fa-crosshairs text-indigo-400 text-lg"></i>
                <div class="flex flex-col">
                  <p class="text-gray-300 text-xs font-semibold hidden sm:block">ความพยายามทั้งหมด</p>
                  <p class="text-yellow-400 text-xs font-bold">
                    {{ assassinationAttempts }}/3
                    <span v-if="assassinationAttempts >= 3" class="text-red-400 text-xxs ml-2">
                      (ครบจำนวน)
                    </span>
                  </p>
                </div>
              </div>
              <!-- Daily Attempts -->
              <div class="flex items-center gap-2 bg-gray-800 p-3 w-full border border-gray-700">
                <i class="fas fa-clock text-indigo-400 text-lg"></i>
                <div class="flex flex-col">
                  <p class="text-gray-300 text-xs font-semibold hidden sm:block">ลอบสังหาร {{ displayName }}</p>
                  <p class="text-yellow-400 text-xs font-bold">
                    {{ dailyAssassinationAttempts }}/6
                    <span v-if="dailyAssassinationAttempts >= 6" class="text-red-400 text-xxs ml-2">
                      (ครบจำนวน)
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <!-- Dice Count Selection (VERTICAL, SMALLER) -->
          <div>
            <p class="text-gray-300 text-xs text-center mb-1">เลือกจำนวนลูกเต๋า</p>
            <div class="grid grid-cols-2 gap-1">
              <button
                v-for="opt in [3,4,5,6]"
                :key="opt"
                @click="selectDiceCount(opt)"
                :disabled="isDiceDisabled(opt)"
                class="py-1 px-2 rounded-md text-xs text-white transition-colors duration-200 border relative"
                :class="diceOptionClass(opt)"
              >
                <div class="flex items-center gap-2">
                  <!-- Dice icons preview -->
                  <div class="flex items-center">
                    <i v-for="i in opt" :key="i" class="fas fa-dice-six text-xs mr-0.5 text-indigo-400"></i>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-xs font-semibold text-gray-300">{{ opt }} ลูกเต๋า</span>
                    <span
                      class="text-xxs text-gray-300 opacity-80"
                      v-if="costFor(opt).amount === 0"
                    >
                      ฟรี
                    </span>
                    <span
                      class="text-xxs text-gray-300 opacity-80"
                      v-else
                    >
                      {{ costFor(opt).amount }}
                      <i v-if="costFor(opt).icon" :class="costFor(opt).icon + ' text-xxs text-white ml-0.5'"></i>
                    </span>
                  </div>
                </div>
                <!-- Selected badge -->
                <span
                  v-if="diceCount === opt"
                  class="absolute top-0 right-0 inline-flex items-center justify-center w-4 h-4 bg-green-600 px-2 py-1 rounded-md border border-green-700"
                >
                  <i class="fas fa-check text-xxs"></i>
                </span>
              </button>
            </div>
          </div>
          <!-- Item Selection for Dice 4 or 5 -->
          <div v-if="diceCount === 4 || diceCount === 5 || diceCount === 6" class="bg-gray-800 p-3 rounded-md border border-gray-700 max-h-48 overflow-y-auto scrollbar-thin">
            <p class="text-gray-300 text-xs text-center mb-2">เลือกไอเท็ม {{ diceAmt }} ชิ้นเพื่อปลดล็อก {{ diceCount }} ลูกเต๋า</p>
            <p class="text-gray-300 text-xxs text-center mb-2">ไอเท็มที่ต้องการ: {{ reqsNames.join(', ') }}</p>
            <div v-if="availableItems.length === 0" class="text-center text-xxs text-yellow-300">
              <i class="fas fa-info-circle mr-1"></i>ไม่มีไอเท็มสำหรับปลดล็อก (คุณไม่มีไอเท็มใดๆ จากรายการที่ต้องการ)
            </div>
            <div v-else class="space-y-2 text-xxs">
              <div
                v-for="invItem in availableItems"
                :key="invItem.id"
                @click="toggleSelection(invItem.id)"
                class="flex items-center gap-2 bg-gray-700 p-1 rounded cursor-pointer hover:bg-gray-600 transition-colors"
                :class="{
                  'border-indigo-500 bg-indigo-900': selectedItemIds.includes(invItem.id),
                  'bg-red-700 cursor-not-allowed pointer-events-none': !selectedItemIds.includes(invItem.id) && selectedNames.includes(invItem.name)
                }"
              >
                <span class="flex-1 truncate">
                  {{ invItem.name }} <span v-if="invItem.level">+{{ invItem.level }}</span> <i v-if="invItem.transfer" class="fa-solid fa-right-left text-green-500 ml-1"></i>
                </span>
                <input
                  type="checkbox"
                  :checked="selectedItemIds.includes(invItem.id)"
                  class="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-600 rounded pointer-events-none"
                />
              </div>
            </div>
            <p class="text-gray-300 text-xs text-center mt-2">เลือกแล้ว: {{ selectedItemIds.length }} / {{ diceAmt }}</p>
          </div>
          <!-- Room Selection -->
          <div>
            <p class="text-gray-300 text-xs text-center mb-1">เลือกห้องที่ผู้รอดชีวิตซ่อนอยู่</p>
            <!-- Search Ghost Button -->
            <div v-if="searchGhostItemId && searchGhostUses === 0" class="text-center mt-2">
              <button
                @click="useSearchGhost"
                :disabled="isUsingSearch"
                class="bg-purple-600 hover:bg-purple-700 text-white text-xs py-1.5 px-3 rounded-md transition-colors duration-200 border border-purple-700 flex items-center justify-center gap-1 mx-auto animate-pulse"
                :class="{ 'animate-pulse opacity-75 cursor-not-allowed': isUsingSearch }"
              >
                <i v-if="!isUsingSearch" class="fas fa-search text-xs"></i>
                <div v-else class="animate-spin rounded-full h-3 w-3 border-t-2 border-white"></div>
                <span v-if="!isUsingSearch">ใช้วิญญาณค้นหาผู้เล่น</span>
                <span v-else>กำลังค้นหา...</span>
              </button>
            </div>
            <!-- Eliminated Rooms Info -->
            <div v-if="searchGhostUses > 0 && eliminatedRooms.length > 0" class="bg-gray-700 p-2 rounded-md mt-2 border border-gray-600 text-center">
              <p class="text-sm text-yellow-300">ห้องที่ผู้รอดชีวิตไม่อยู่: {{ eliminatedRooms.join(', ') }}</p>
            </div>
            <div class="grid grid-cols-5 gap-1">
              <button
                v-for="roomNum in Array.from({ length: survivor.room }, (_, i) => i + 1)"
                :key="roomNum"
                @click="selectRoom(roomNum)"
                class="bg-gray-800 p-1 rounded-md border border-gray-700 hover:border-indigo-600 hover:bg-gray-900 transition-all duration-200"
                :class="{
                  'border-2 border-indigo-600 bg-gray-900': selectedRoom === roomNum,
                  'opacity-50 cursor-not-allowed bg-red-900 border-gray-500': eliminatedRooms.includes(roomNum)
                }"
                :disabled="isRolling || assassinationAttempts >= 3 || dailyAssassinationAttempts >= 6 || eliminatedRooms.includes(roomNum)"
              >
                <i class="fas fa-door-closed text-gray-300 text-xs"></i>
                <span class="block text-xxs font-semibold text-gray-300 mt-0.5">{{ roomNum }}</span>
              </button>
            </div>
          </div>
          <!-- Assassination Action -->
          <div>
            <!-- Dice Animation -->
            <div v-if="isRolling" class="text-center text-gray-300">
              <div class="dice-scene flex justify-center gap-2 mt-4 flex-wrap">
                <!-- Player Dice -->
                <div
                  v-for="(die, index) in tempPlayerDices"
                  :key="'player-' + index"
                  class="cube player-cube animate-dice-roll"
                >
                  <div class="face front">{{ die }}</div>
                  <div class="face back">{{ die }}</div>
                  <div class="face right">{{ die }}</div>
                  <div class="face left">{{ die }}</div>
                  <div class="face top">{{ die }}</div>
                  <div class="face bottom">{{ die }}</div>
                </div>
                <!-- Survivor Dice -->
                <div
                  v-for="(die, index) in tempSurvivorDices"
                  :key="'survivor-' + index"
                  class="cube survivor-cube animate-dice-roll"
                  :class="{ 'delay-200': index % 2 === 1 }"
                >
                  <div class="face front">{{ die }}</div>
                  <div class="face back">{{ die }}</div>
                  <div class="face right">{{ die }}</div>
                  <div class="face left">{{ die }}</div>
                  <div class="face top">{{ die }}</div>
                  <div class="face bottom">{{ die }}</div>
                </div>
              </div>
            </div>
          </div>
          <!-- Confirm and Back Buttons -->
          <div v-if="!isRolling" class="flex flex-col gap-2">
            <button
              @click="assassinate"
              :disabled="!selectedRoom || isRolling || !isPvpEnabled || assassinationAttempts >= 3 || dailyAssassinationAttempts >= 6 || (diceCount > 1 && (characterPublic.silver || 0) < (diceCount === 2 ? 300 : 1500)) || ((diceCount === 4 || diceCount === 5 || diceCount === 6) && selectedItemIds.length !== diceAmt)"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 border border-indigo-700"
            >
              <i class="fas fa-user-ninja text-xs"></i>
              <span v-if="!isRolling && isPvpEnabled && assassinationAttempts < 3 && dailyAssassinationAttempts < 6">
                ยืนยันลอบสังหาร {{ displayName }}
              </span>
              <span v-else-if="!isRolling && !isPvpEnabled">
                ขณะนี้ปิดระบบ PVP ชั่วคราว
              </span>
              <span v-else-if="!isRolling && assassinationAttempts >= 3">
                ครบ 3 ครั้ง กรุณาค้นหาใหม่
              </span>
              <span v-else-if="!isRolling && dailyAssassinationAttempts >= 6">
                ครบ 6 ครั้งวันนี้
              </span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>
            <button
              @click="activeTab = 'home'"
              class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600 flex items-center justify-center gap-2"
            >
              <i class="fas fa-arrow-left text-xs"></i> ย้อนกลับ
            </button>
          </div>
        </div>
        <!-- Tab: Result -->
        <div v-else-if="activeTab === 'result'" class="flex flex-col gap-4">
          <!-- Not Encountered -->
          <div v-if="!isEncountered" class="text-center text-gray-300 py-6">
            <i class="fas fa-times-circle text-red-400 text-2xl mb-2"></i>
            <p class="text-sm">ไม่พบผู้รอดชีวิตในห้องที่เลือก</p>
            <p class="text-xxs sm:text-xs text-gray-400 mt-1">{{ fightResult[0]?.message || 'ไม่มีข้อมูลผลลัพธ์' }}</p>
          </div>
          <!-- Encountered -->
          <div v-else class="flex flex-col gap-4">
            <!-- Dice Results -->
            <div class="text-gray-300 text-center">
              <div v-if="playerDiceResults.length || survivorDiceResults.length" class="mb-4">
                <!-- Player Dice -->
                <div class="dice-scene flex justify-center items-center gap-2 flex-wrap mb-2">
                  <div
                    v-for="(die, index) in playerDiceResults"
                    :key="'player-result-' + index"
                    class="cube player-cube"
                  >
                    <div class="face front">{{ die }}</div>
                    <div class="face back">{{ die }}</div>
                    <div class="face right">{{ die }}</div>
                    <div class="face left">{{ die }}</div>
                    <div class="face top">{{ die }}</div>
                    <div class="face bottom">{{ die }}</div>
                  </div>
                  <span
                    v-if="playerDiceResults.length"
                    class="text-sm font-semibold bg-indigo-600 px-2 py-1 rounded-md border border-indigo-700"
                  >
                    รวม: {{ playerDiceResults.reduce((sum, die) => sum + die, 0) }}
                  </span>
                </div>
                <!-- Survivor Dice -->
                <div class="dice-scene flex justify-center items-center gap-2 flex-wrap">
                  <div
                    v-for="(die, index) in survivorDiceResults"
                    :key="'survivor-result-' + index"
                    class="cube survivor-cube"
                  >
                    <div class="face front">{{ die }}</div>
                    <div class="face back">{{ die }}</div>
                    <div class="face right">{{ die }}</div>
                    <div class="face left">{{ die }}</div>
                    <div class="face top">{{ die }}</div>
                    <div class="face bottom">{{ die }}</div>
                  </div>
                  <span
                    v-if="survivorDiceResults.length"
                    class="text-sm font-semibold bg-indigo-600 px-2 py-1 rounded-md border border-indigo-700"
                  >
                    รวม: {{ survivorDiceResults.reduce((sum, die) => sum + die, 0) }}
                  </span>
                </div>
              </div>
              <!-- Fight Events -->
              <div class="space-y-2 max-h-60 overflow-y-auto scrollbar-thin">
                <div
                  v-for="(event, index) in fightResult"
                  :key="index"
                  class="p-2 bg-gray-800 rounded-md border border-gray-700 animate-fadeIn"
                  :class="{
                    'border-l-4 border-orange-400': event.type === 'trap',
                    'border-l-4 border-indigo-400': event.type === 'dice',
                    'border-l-4 border-red-400': event.type === 'attack' && event.target === 'player',
                    'border-l-4 border-green-400': event.type === 'attack' && event.target === 'survivor',
                    'border-l-4 border-blue-400': event.type === 'reward',
                    'border-l-4 border-purple-400': event.type === 'status',
                    'border-l-4 border-teal-400': event.type === 'guardian_protect',
                    'border-l-4 border-amber-400': event.type === 'guardian_destroyed'
                  }"
                >
                  <!-- Trap Event -->
                  <div v-if="event.type === 'trap'" class="flex items-center gap-2">
                    <i class="fas fa-bolt text-orange-400"></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                  <!-- Dice Event -->
                  <div v-if="event.type === 'dice'" class="flex items-center gap-2">
                    <i class="fas fa-dice text-indigo-400"></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                  <!-- Attack Event -->
                  <div v-if="event.type === 'attack'" class="flex items-center gap-2">
                    <i
                      :class="{
                        'fas fa-fist-raised': !event.isCritical && !event.isEvaded,
                        'fas fa-burst': event.isCritical,
                        'fas fa-shield-alt': event.isEvaded,
                        'text-red-400': event.target === 'player',
                        'text-green-400': event.target === 'survivor'
                      }"
                    ></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                  <!-- Reward Event -->
                  <div v-if="event.type === 'reward'" class="flex items-center gap-2">
                    <i class="fas fa-gem text-blue-400"></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                  <!-- Status Event -->
                  <div v-if="event.type === 'status'" class="flex items-center gap-2">
                    <i
                      :class="{
                        'fas fa-skull': event.status === 'death',
                        'fas fa-check': event.status === 'success',
                        'fas fa-times': event.status === 'failure' || event.status === 'neutral',
                        'text-purple-400': true
                      }"
                    ></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                  <!-- Guardian Protect Event -->
                  <div v-if="event.type === 'guardian_protect'" class="flex items-center gap-2">
                    <i class="fas fa-shield-alt text-teal-400"></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                  <!-- Guardian Destroyed Event -->
                  <div v-if="event.type === 'guardian_destroyed'" class="flex items-center gap-2">
                    <i class="fas fa-shield-alt text-amber-400"></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Close Button -->
          <button
            @click="$emit('close')"
            class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600 flex items-center justify-center gap-2"
          >
            <i class="fas fa-times text-xs"></i> ปิด
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import gameItemsData from '@/data/gameItems.json';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
export default {
  name: 'AssassinationModal',
  props: {
    survivorId: {
      type: String,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const survivor = ref(null);
    const survivorEvent = ref(null);
    const character = ref(null);
    const characterPublic = ref(null);
    const isLoading = ref(true);
    const error = ref(null);
    const activeTab = ref('home');
    const selectedRoom = ref(null);
    const fightResult = ref([]);
    const playerDiceResults = ref([]);
    const survivorDiceResults = ref([]);
    const survivorDiceCount = ref(1);
    const isRolling = ref(false);
    const isEncountered = ref(false);
    const tempPlayerDices = ref([]);
    const tempSurvivorDices = ref([]);
    const playerHp = ref(null);
    const playerMaxHp = ref(null);
    const diceCount = ref(3);
    const isPvpEnabled = ref(true);
    const assassinationAttempts = ref(0);
    const dailyAssassinationAttempts = ref(0);
    const eliminatedRooms = ref([]); // New ref for eliminatedRooms
    let diceInterval;
    const anonymousImage = 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2Fanonymouse.png?alt=media&token=25259b2d-03f1-49fb-b70c-054cd88d2a86';
    // Search Ghost refs
    const searchGhostItemId = ref(null);
    const searchGhostUses = ref(0);
    const isUsingSearch = ref(false);
    // Item selection for dice 4 or 5
    const localInventory = ref([]);
    const selectedItemIds = ref([]);
    const requirements = ref([]); // For 4 dice
    const amount = ref(1); // For 4 dice
    const requirementsFive = ref([]); // For 5 dice
    const amountFive = ref(1); // For 5 dice
    const requirementsSix = ref([]); // For 6 dice
    const amountSix = ref(1); // For 6 dice
    const fetchRequirements = async () => {
      try {
        const unlockDoc = await projectFirestore.collection('server').doc('unlockFourDice').get();
        if (unlockDoc.exists) {
          requirements.value = unlockDoc.data().requirements || [];
          amount.value = unlockDoc.data().amount || 1;
        }
      } catch (err) {
        console.error('Error fetching unlock four dice requirements:', err);
      }
    };
    const fetchRequirementsFive = async () => {
      try {
        const unlockDoc = await projectFirestore.collection('server').doc('unlockFiveDice').get();
        if (unlockDoc.exists) {
          requirementsFive.value = unlockDoc.data().requirements || [];
          amountFive.value = unlockDoc.data().amount || 1;
        }
      } catch (err) {
        console.error('Error fetching unlock five dice requirements:', err);
      }
    };
    const fetchRequirementsSix = async () => {
      try {
        const unlockDoc = await projectFirestore.collection('server').doc('unlockSixDice').get();
        if (unlockDoc.exists) {
          requirementsSix.value = unlockDoc.data().requirements || [];
          amountSix.value = unlockDoc.data().amount || 1;
        }
      } catch (err) {
        console.error('Error fetching unlock six dice requirements:', err);
      }
    };
    const fetchSurvivor = async () => {
      try {
        const doc = await projectFirestore.collection('characters').doc(props.survivorId).get();
        if (doc.exists) {
          survivor.value = { docId: doc.id, ...doc.data() };
          // Fetch survivor event if exists
          if (survivor.value.event) {
            const eventDoc = await projectFirestore.collection('events').doc(survivor.value.event).get();
            if (eventDoc.exists) {
              survivorEvent.value = eventDoc.data();
            }
          }
        } else {
          error.value = 'ไม่พบข้อมูลของผู้รอดชีวิตนี้';
        }
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลผู้รอดชีวิต: ' + err.message;
      }
    };
    const inventoryStore = useInventoryStore();

    const updateLocalInventory = () => {
      const bagData = inventoryStore.bagData;
      if (!bagData || !bagData.items) {
        localInventory.value = [];
        return;
      }
      const bagItems = bagData.items;
      localInventory.value = Object.entries(bagItems).map(([instanceId, item]) => {
        const tpl = gameItemsData[item.templateId] || {};
        return {
          id: instanceId,
          instanceId,
          name: tpl.name || item.templateId,
          templateId: item.templateId,
          weight: tpl.weight || 0,
          type: tpl.type || null,
          ...tpl,
          ...(item.states || {}),
        };
      }).filter(item => item && item.name);
    };

    watch(() => inventoryStore.bagData, updateLocalInventory, { immediate: true });
    const fetchCharacterPrivate = async () => {
      if (user.value) {
        const data = playerStore.characterPrivateData;
        if (data) {
          character.value = data;
          eliminatedRooms.value = data.eliminatedRooms || [];
          searchGhostUses.value = data.searchGhostUses || 0;
          // Set target if not matching
          if (data.assassinateSurvivorUid !== props.survivorId) {
            await projectFirestore.collection('characterPrivate').doc(user.value.uid).update({
              assassinateSurvivorUid: props.survivorId
            });
            character.value.assassinateSurvivorUid = props.survivorId;
          }
          // Find search ghost (v2: read from ghostItems object)
          const ghostItems = data.ghostItems || {};
          const searchGhostEntry = Object.entries(ghostItems).find(([instanceId, item]) => {
            const tpl = gameItemsData[item.templateId] || {};
            return tpl.subtype === 'วิญญาณค้นหาผู้เล่น';
          });
          searchGhostItemId.value = searchGhostEntry?.[0] || null;
          const stamina =
            (data.itemStats?.stamina || 0) +
            (data.bonusStats?.stamina || 0) +
            (data.stats?.stamina || 0) +
            (data.traitStats?.stamina || 0) +
            (data.skillStats?.stamina || 0) +
            (data.titleStats?.stamina || 0) +
            (data.tattooStats?.stamina || 0) +
            (data.ghostStats?.stamina || 0);
          const flatMaxHp =
            (data.itemStats?.maxHp || 0) +
            (data.bonusStats?.maxHp || 0) +
            (data.stats?.maxHp || 0) +
            (data.traitStats?.maxHp || 0) +
            (data.skillStats?.maxHp || 0) +
            (data.titleStats?.maxHp || 0) +
            (data.tattooStats?.maxHp || 0) +
            (data.ghostStats?.maxHp || 0);
          playerMaxHp.value = stamina * 20 + flatMaxHp;
          playerHp.value = Math.max(0, playerMaxHp.value - (data.damageTaken || 0));
          assassinationAttempts.value = data.assassinationAttempts || 0;
          dailyAssassinationAttempts.value = data.dailyAssassinations?.[props.survivorId] || 0;
        } else {
          error.value = 'ไม่พบข้อมูลตัวละคร (Private)';
        }
      }
    };
    const fetchCharacterPublic = async () => {
      if (user.value) {
        try {
          const charPublicDoc = await projectFirestore
            .collection('characters')
            .doc(user.value.uid)
            .get();
          if (charPublicDoc.exists) {
            characterPublic.value = charPublicDoc.data();
          } else {
            error.value = 'ไม่พบข้อมูลตัวละคร (Public)';
          }
        } catch (err) {
          error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลตัวละคร (Public): ' + err.message;
        }
      }
    };
    const fetchPvpSetting = async () => {
      try {
        const pvpDoc = await projectFirestore.collection('server').doc('setting').get();
        if (pvpDoc.exists) {
          isPvpEnabled.value = pvpDoc.data().pvp === true;
        } else {
          isPvpEnabled.value = false;
          error.value = 'ไม่พบการตั้งค่า PVP';
        }
      } catch (err) {
        isPvpEnabled.value = false;
        error.value = 'เกิดข้อผิดพลาดในการโหลดการตั้งค่า PVP: ' + err.message;
      }
    };
    const checkAssassinateSurvivorUid = async () => {
      if (fightResult.value.length > 0) {
        activeTab.value = 'result';
        return;
      }
      const assassinateSurvivorUid = playerStore.characterPrivateData?.assassinateSurvivorUid;
      if (assassinateSurvivorUid !== props.survivorId && fightResult.value.length > 0) {
        activeTab.value = 'result';
      } else if (assassinateSurvivorUid !== props.survivorId) {
        activeTab.value = 'home';
      }
    };
    const useSearchGhost = async () => {
      if (!user.value || !searchGhostItemId.value) {
        error.value = 'ไม่พบวิญญาณค้นหาผู้เล่น';
        return;
      }
      if (searchGhostUses.value > 0) {
        error.value = 'วิญญาณค้นหาผู้เล่นไม่พร้อมใช้งาน';
        return;
      }
      isUsingSearch.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'ghostCommandAction', subAction: 'useMagic', instanceId: searchGhostItemId.value });
        if (result?.data?.success) {
          eliminatedRooms.value = result.data.eliminatedRooms || [];
          searchGhostUses.value = 1;
        } else {
          error.value = result?.data?.message || 'การใช้เวทย์มนตร์ล้มเหลว';
        }
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการใช้เวทย์มนตร์';
      } finally {
        isUsingSearch.value = false;
      }
    };
    const characterStats = computed(() => {
      if (!character.value) return { maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0, flee: 0, criticalReduction: 0 };
      const ghostStats = character.value.ghostStats || { stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 };
      const str =
        (character.value.itemStats?.str || 0) +
        (character.value.bonusStats?.str || 0) +
        (character.value.stats?.str || 0) +
        (character.value.traitStats?.str || 0) +
        (character.value.skillStats?.str || 0) +
        (character.value.titleStats?.str || 0) +
        (character.value.tattooStats?.str || 0) +
        (ghostStats.str || 0);
      const stamina =
        (character.value.itemStats?.stamina || 0) +
        (character.value.bonusStats?.stamina || 0) +
        (character.value.stats?.stamina || 0) +
        (character.value.traitStats?.stamina || 0) +
        (character.value.skillStats?.stamina || 0) +
        (character.value.titleStats?.stamina || 0) +
        (character.value.tattooStats?.stamina || 0) +
        (ghostStats.stamina || 0);
      const int =
        (character.value.itemStats?.int || 0) +
        (character.value.bonusStats?.int || 0) +
        (character.value.stats?.int || 0) +
        (character.value.traitStats?.int || 0) +
        (character.value.skillStats?.int || 0) +
        (character.value.titleStats?.int || 0) +
        (character.value.tattooStats?.int || 0) +
        (ghostStats.int || 0);
      const agi =
        (character.value.itemStats?.agi || 0) +
        (character.value.bonusStats?.agi || 0) +
        (character.value.stats?.agi || 0) +
        (character.value.traitStats?.agi || 0) +
        (character.value.skillStats?.agi || 0) +
        (character.value.titleStats?.agi || 0) +
        (character.value.tattooStats?.agi || 0) +
        (ghostStats.agi || 0);
      const luck =
        (character.value.itemStats?.luck || 0) +
        (character.value.bonusStats?.luck || 0) +
        (character.value.stats?.luck || 0) +
        (character.value.traitStats?.luck || 0) +
        (character.value.skillStats?.luck || 0) +
        (character.value.titleStats?.luck || 0) +
        (character.value.tattooStats?.luck || 0) +
        (ghostStats.luck || 0);
      const defense =
        (character.value.itemStats?.defense || 0) +
        (character.value.bonusStats?.defense || 0) +
        (character.value.stats?.defense || 0) +
        (character.value.traitStats?.defense || 0) +
        (character.value.skillStats?.defense || 0) +
        (character.value.titleStats?.defense || 0) +
        (character.value.tattooStats?.defense || 0) +
        (ghostStats.defense || 0);
      const flatMaxHp =
        (character.value.itemStats?.maxHp || 0) +
        (character.value.bonusStats?.maxHp || 0) +
        (character.value.stats?.maxHp || 0) +
        (character.value.traitStats?.maxHp || 0) +
        (character.value.skillStats?.maxHp || 0) +
        (character.value.titleStats?.maxHp || 0) +
        (character.value.tattooStats?.maxHp || 0) +
        (ghostStats.maxHp || 0);
      const flatAtk =
        (character.value.itemStats?.atk || 0) +
        (character.value.bonusStats?.atk || 0) +
        (character.value.stats?.atk || 0) +
        (character.value.traitStats?.atk || 0) +
        (character.value.skillStats?.atk || 0) +
        (character.value.titleStats?.atk || 0) +
        (character.value.tattooStats?.atk || 0) +
        (ghostStats.atk || 0);
      const flatCritical =
        (character.value.itemStats?.critical || 0) +
        (character.value.bonusStats?.critical || 0) +
        (character.value.stats?.critical || 0) +
        (character.value.traitStats?.critical || 0) +
        (character.value.skillStats?.critical || 0) +
        (character.value.titleStats?.critical || 0) +
        (character.value.tattooStats?.critical || 0) +
        (ghostStats.critical || 0);
      const flatEvasion =
        (character.value.itemStats?.evasion || 0) +
        (character.value.bonusStats?.evasion || 0) +
        (character.value.stats?.evasion || 0) +
        (character.value.traitStats?.evasion || 0) +
        (character.value.skillStats?.evasion || 0) +
        (character.value.titleStats?.evasion || 0) +
        (character.value.tattooStats?.evasion || 0) +
        (ghostStats.evasion || 0);
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: stamina * 20 + flatMaxHp,
        atk: str * 2 + flatAtk,
        critical: Math.floor((int / 2) + flatCritical),
        evasion: Math.floor((agi / 2) + flatEvasion),
        defense: defense,
        flee: Math.floor((agi + int) / 4),
        criticalReduction: Math.floor(luck) + defenseBonus,
        damageReduction: defenseBonus
      };
    });
    const displayName = computed(() => {
      if (!survivor.value || characterPublic.value?.building === 1 || characterPublic.value?.building === 2) return 'บุคคลลึกลับ';
      return survivor.value.characterName || 'บุคคลลึกลับ';
    });
    const displayImage = computed(() => {
      if (!survivor.value || characterPublic.value?.building === 1 || characterPublic.value?.building === 2) return anonymousImage;
      return survivor.value.imageUrl || anonymousImage;
    });
    const displayProtectCount = computed(() => {
      if (survivorEvent.value && survivorEvent.value.type === 'guardianGhost') {
        return survivorEvent.value.ghost?.protectCount || 0;
      }
      return '??';
    });
    const displayDiamond = computed(() => {
      if (characterPublic.value?.building === 1 || characterPublic.value?.building === 2) return 0;
      return survivor.value?.diamond || 0;
    });
    const displayGold = computed(() => {
      if (characterPublic.value?.building === 1 || characterPublic.value?.building === 2) return 0;
      return survivor.value?.gold || 0;
    });
    const roomDifference = computed(() => {
      const pr = Number(characterPublic.value?.room || 0);
      const sr = Number(survivor.value?.room || 0);
      return Math.abs(pr - sr);
    });
    const lossRate = computed(() => {
      const d = roomDifference.value;
      if (d === 0) return 0.5;
      if (d === 1) return 0.4;
      if (d === 2) return 0.3;
      if (d === 3) return 0.2;
      if (d === 4) return 0.1;
      return 0;
    });
    const lossPercent = computed(() => Math.round(lossRate.value * 100));
    const diamondStolen = computed(() => Math.floor((displayDiamond.value || 0) * lossRate.value));
    const diamondTax = computed(() => Math.floor(diamondStolen.value * 0.3));
    const netDiamond = computed(() => Math.max(0, diamondStolen.value - diamondTax.value));
    const goldStolen = computed(() => Math.floor((displayGold.value || 0) * lossRate.value));
    const goldTax = computed(() => Math.floor(goldStolen.value * 0.3));
    const netGold = computed(() => Math.max(0, goldStolen.value - goldTax.value));
    const selectRoom = (roomNumber) => {
      selectedRoom.value = roomNumber;
      fightResult.value = [];
      playerDiceResults.value = [];
      survivorDiceResults.value = [];
      isEncountered.value = false;
    };
    const selectDiceCount = (count) => {
      diceCount.value = count;
      selectedItemIds.value = [];
    };
    const reqsNames = computed(() => diceCount.value === 4 ? requirements.value : diceCount.value === 5 ? requirementsFive.value : requirementsSix.value);
    const availableItems = computed(() => {
      const reqs = reqsNames.value;
      return localInventory.value.filter(invItem => reqs.includes(invItem.name));
    });
    const selectedNames = computed(() => {
      return selectedItemIds.value.map(sid => {
        const sItem = localInventory.value.find(i => i.id === sid);
        return sItem ? sItem.name : null;
      }).filter(name => name !== null);
    });
    const diceAmt = computed(() => diceCount.value === 4 ? amount.value : diceCount.value === 5 ? amountFive.value : amountSix.value);
    const toggleSelection = (id) => {
      const index = selectedItemIds.value.indexOf(id);
      if (index !== -1) {
        selectedItemIds.value.splice(index, 1);
      } else if (selectedItemIds.value.length < diceAmt.value) {
        const item = localInventory.value.find(i => i.id === id);
        if (item) {
          const selectedNamesList = selectedItemIds.value.map(sid => {
            const sItem = localInventory.value.find(i => i.id === sid);
            return sItem ? sItem.name : null;
          }).filter(name => name !== null);
          if (!selectedNamesList.includes(item.name)) {
            selectedItemIds.value.push(id);
          }
        }
      }
    };
    const hasItemForDice = computed(() => availableItems.value.length >= diceAmt.value);
    const isDiceDisabled = (count) => {
      if (assassinationAttempts.value >= 3 || dailyAssassinationAttempts.value >= 6) return true;
      if (count === 3) return false;
      if (count === 4 || count === 5 || count === 6) return false; // Allow always to view requirements
      const need = count === 2 ? 300 : 1500;
      return (characterPublic.value?.silver || 0) < need;
    };
    const diceOptionClass = (opt) => {
      const base = 'relative flex-1 py-2 px-3 rounded-md border text-white text-left transition-all duration-200';
      const selected = diceCount.value === opt
        ? 'bg-green-700 border-green-800 shadow'
        : 'bg-gray-800 hover:bg-gray-700 border-gray-700';
      const disabled = isDiceDisabled(opt) ? 'opacity-60 cursor-not-allowed' : '';
      return `${base} ${selected} ${disabled}`;
    };
    const costFor = (opt) => {
      if (opt === 3) return { amount: 0, icon: '' };
      if (opt === 4) return { amount: 'ไอเท็ม ' + amount.value + ' ชิ้น', icon: '' };
      if (opt === 5) return { amount: 'ไอเท็ม ' + amountFive.value + ' ชิ้น', icon: '' };
      if (opt === 6) return { amount: 'ไอเท็ม ' + amountSix.value + ' ชิ้น', icon: '' };
      return { amount: opt === 2 ? 300 : 1500, icon: 'fas fa-coins' };
    };
    const startDiceAnimation = () => {
      isRolling.value = true;
      diceInterval = setInterval(() => {
        tempPlayerDices.value = Array.from(
          { length: diceCount.value },
          () => Math.floor(Math.random() * 6) + 1
        );
        tempSurvivorDices.value = Array.from(
          { length: survivorDiceCount.value },
          () => Math.floor(Math.random() * 6) + 1
        );
      }, 100);
    };
    const stopDiceAnimation = () => {
      clearInterval(diceInterval);
      isRolling.value = false;
    };
    const assassinate = async () => {
      if (!selectedRoom.value || !isPvpEnabled.value || assassinationAttempts.value >= 3 || dailyAssassinationAttempts.value >= 6) return;
      if (diceCount.value > 1 && diceCount.value !== 4 && diceCount.value !== 5 && diceCount.value !== 6 && (characterPublic.value.silver || 0) < (diceCount.value === 2 ? 300 : 1500)) {
        error.value = 'Silver ไม่เพียงพอ';
        return;
      }
      if ((diceCount.value === 4 || diceCount.value === 5 || diceCount.value === 6) && selectedItemIds.value.length !== diceAmt.value) {
        error.value = 'กรุณาเลือกไอเท็มให้ครบ ' + diceAmt.value + ' ชิ้นสำหรับ ' + diceCount.value + ' ลูกเต๋า';
        return;
      }
      try {
        startDiceAnimation();
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const response = await projectFunctions.httpsCallable('gameActions')({
          action: 'assassinateSurvivor',
          survivorId: props.survivorId,
          selectedRoom: selectedRoom.value,
          diceCount: diceCount.value,
          currencyType: diceCount.value > 1 && diceCount.value !== 4 && diceCount.value !== 5 && diceCount.value !== 6 ? 'silver' : null,
          instanceIds: (diceCount.value === 4 || diceCount.value === 5 || diceCount.value === 6) ? selectedItemIds.value : null,
        });
        stopDiceAnimation();
        fightResult.value = response.data.fightResult || [];
        playerDiceResults.value = response.data.playerDiceResults || [];
        survivorDiceResults.value = response.data.survivorDiceResults || [];
        survivorDiceCount.value = response.data.survivorDiceCount || 1;
        isEncountered.value = response.data.isEncountered || false;
        assassinationAttempts.value = response.data.totalAssassinationAttempts || 0;
        dailyAssassinationAttempts.value = response.data.dailyAssassinationAttempts || 0;
        selectedRoom.value = null;
        if ((diceCount.value === 4 || diceCount.value === 5 || diceCount.value === 6) && selectedItemIds.value.length > 0) {
          localInventory.value = localInventory.value.filter(item => !selectedItemIds.value.includes(item.id));
          selectedItemIds.value = [];
        }
        await fetchCharacterPrivate();
        await fetchCharacterPublic();
        activeTab.value = 'result';
        if (assassinationAttempts.value >= 3 || dailyAssassinationAttempts.value >= 6) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          emit('close');
        }
      } catch (err) {
        stopDiceAnimation();
        error.value = 'เกิดข้อผิดพลาดในการลอบสังหาร: ' + err.message;
      }
    };
    onMounted(async () => {
      isLoading.value = true;
      await Promise.all([
        fetchRequirements(),
        fetchRequirementsFive(),
        fetchRequirementsSix(),
        fetchSurvivor(),
        fetchCharacterPrivate(),
        fetchCharacterPublic(),
        fetchPvpSetting(),
      ]);
      isLoading.value = false;
      await checkAssassinateSurvivorUid();
    });
    onUnmounted(() => {
      if (diceInterval) clearInterval(diceInterval);
    });
    watch([survivor, characterPublic], () => {
      // no-op; computed properties will recalc automatically
    });
    return {
      survivor,
      survivorEvent,
      character,
      characterPublic,
      isLoading,
      error,
      activeTab,
      selectedRoom,
      selectRoom,
      selectDiceCount,
      diceCount,
      assassinate,
      fightResult,
      playerDiceResults,
      survivorDiceResults,
      survivorDiceCount,
      isRolling,
      isEncountered,
      tempPlayerDices,
      tempSurvivorDices,
      playerHp,
      playerMaxHp,
      characterStats,
      diamondStolen,
      diamondTax,
      netDiamond,
      goldStolen,
      goldTax,
      netGold,
      displayName,
      displayImage,
      displayProtectCount,
      displayDiamond,
      displayGold,
      roomDifference,
      lossPercent,
      diamondReward: diamondStolen,
      goldReward: goldStolen,
      isDiceDisabled,
      diceOptionClass,
      costFor,
      isPvpEnabled,
      assassinationAttempts,
      dailyAssassinationAttempts,
      eliminatedRooms, // Return eliminatedRooms for template
      // Search Ghost
      searchGhostItemId,
      searchGhostUses,
      isUsingSearch,
      useSearchGhost,
      // Item selection
      localInventory,
      requirements,
      amount,
      requirementsFive,
      amountFive,
      requirementsSix,
      amountSix,
      selectedItemIds,
      availableItems,
      diceAmt,
      toggleSelection,
      hasItemForDice,
      reqsNames,
      selectedNames
    };
  },
};
</script>
<style scoped>
.cube {
  position: relative;
  width: 40px;
  height: 40px;
  transform-style: preserve-3d;
  transition: transform 1s;
  margin: 0 auto;
  perspective: 800px;
}
.cube .face {
  position: absolute;
  width: 40px;
  height: 40px;
  line-height: 40px;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
  backface-visibility: hidden;
}
.cube .face.front { transform: rotateY(0deg) translateZ(20px); }
.cube .face.back { transform: rotateY(180deg) translateZ(20px); }
.cube .face.right { transform: rotateY(90deg) translateZ(20px); }
.cube .face.left { transform: rotateY(-90deg) translateZ(20px); }
.cube .face.top { transform: rotateX(90deg) translateZ(20px); }
.cube .face.bottom { transform: rotateX(-90deg) translateZ(20px); }
.player-cube .face {
  background: linear-gradient(145deg, #3b82f6, #1e40af);
}
.survivor-cube .face {
  background: linear-gradient(145deg, #ef4444, #b91c1c);
}
@keyframes diceRoll {
  0% { transform: rotateX(0deg) rotateY(0deg); }
  100% { transform: rotateX(360deg) rotateY(360deg); }
}
.animate-dice-roll { animation: diceRoll 1s linear infinite; }
.delay-200 { animation-delay: 0.2s; }
.animate-spin { animation: spin 1s linear infinite; }
.animate-spin-pulse { animation: spin-pulse 1.2s ease-in-out infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes spin-pulse { 0% { transform: rotate(0deg) scale(1); } 50% { transform: rotate(180deg) scale(1.2); } 100% { transform: rotate(360deg) scale(1); } }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-fade-in { animation: fadeIn 0.3s ease-out; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }
.animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
button:not(:disabled):active { transform: scale(0.98); }
button:disabled { opacity: 0.6; cursor: not-allowed; pointer-events: none; }
.scrollbar-thin::-webkit-scrollbar { width: 6px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 3px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4c51bf; border-radius: 3px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #5a67d8; }
</style>
